/* See: type declaration of react-table is needed in order to be able to use
  sort props and handlers, from react-table, then they should be included in the bundle,
  but for webpack original types have precedence over declared types.
  This is the reason why reference tag is declared here.
*/
/* eslint-disable-next-line @typescript-eslint/triple-slash-reference */
/// <reference path="../../../../../@types/react-table-config.d.ts" />

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TableOptions, useFlexLayout, useSortBy, useTable } from 'react-table'
import {
  Table as ChakraTable,
  Tbody,
  Td,
  Thead,
  Tr,
  Stack,
  Center,
  Flex,
} from '@chakra-ui/react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Spinner } from '../Spinner'
import { ITableProps, TTableRowData } from './types'
import { TableHeader } from './TableHeader'
import { TableRow } from './TableRow'

// Fallback to have a table height in case no calculation is possible
const TABLE_INITIAL_HEIGHT = 500
// Padding at the bottom to make the table nicer and not cutting the last element
const INNER_TABLE_BOTTOM_PADDING = 30

function Table<RowData extends TTableRowData>({
  onChangeSort,
  data,
  columns,
  initialState,
  isLoading,
  sort,
  renderHeader = (column) => <TableHeader column={column} key={column.id} />,
  renderTableRow = ({ row }) => <TableRow key={row.id} row={row} />,
  emptyTableComponent,
  bodyStyle,
  id,
  loadMore,
  isFetchingNextPage
}: ITableProps<RowData>): JSX.Element {
  const useControlledState = useCallback<
    Exclude<TableOptions<RowData>['useControlledState'], undefined>
  >(
    (state) => {
      if (!sort) {
        return state
      }

      return { ...state, sortBy: sort.slice() }
    },
    [sort],
  )

  // This is to have a fixed height so react-virtual can work properly
  const [tableHeight, setTableHeight] = useState(TABLE_INITIAL_HEIGHT)
  const tableClient = document.getElementById(id)?.parentElement
  const tableClientHeight = tableClient?.offsetHeight

  const tableHeaderId = `table-head-${id}`
  const headerHeight = document.getElementById(tableHeaderId)?.offsetHeight
  const headerOffsetTop = document.getElementById(tableHeaderId)?.offsetTop ?? 0

  useEffect(() => {
    const handleResize = () => {
      if (!isLoading && tableClientHeight && headerHeight) {
        const initialTableHeight =
          window.innerHeight -
          headerOffsetTop -
          headerHeight -
          INNER_TABLE_BOTTOM_PADDING

        setTableHeight(initialTableHeight)
      }
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [headerHeight, headerOffsetTop, isLoading, tableClient, tableClientHeight])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState,
      useControlledState,
    },
    useFlexLayout,
    useSortBy,
  )

  useEffect(() => {
    onChangeSort?.(sortBy)
  }, [onChangeSort, sortBy])

  const renderedRows = useMemo(
    () =>
      rows.map((row) => {
        prepareRow(row)

        return renderTableRow({ row })
      }),
    [prepareRow, renderTableRow, rows],
  )

  const parentRef = useRef() as React.MutableRefObject<HTMLTableSectionElement>

  const rowVirtualizer = useVirtualizer({
    count: renderedRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 73,
    overscan: 10,
  })



  useEffect(() => {
    const reference = parentRef.current;
    const onScroll = () => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (reference) {
        const { scrollTop, scrollHeight, clientHeight } = reference
        const isNearBottom = scrollTop + clientHeight + 1 >= scrollHeight

        if (isNearBottom) {
          loadMore?.()
        }
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (reference) {
      reference.addEventListener("scroll", onScroll);

      return () => {
        reference.removeEventListener("scroll", onScroll);
      };
    }
  }, []);

  const displayRows = useMemo(() => {
    return rowVirtualizer.getVirtualItems().map((virtualRow) => (
      <div
        key={virtualRow.index}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: `${virtualRow.size}px`,
          transform: `translateY(${virtualRow.start}px)`,
        }}>
        {renderedRows[virtualRow.index]}
      </div>
    ))
  }, [renderedRows, rowVirtualizer])

  const isTableEmpty = rows.length === 0

  const tableBodyHeight = isTableEmpty
    ? '100%'
    : `${rowVirtualizer.getTotalSize()}px`

  return (
    <ChakraTable as="div" role="table" {...getTableProps()} id={id}>
      <Thead as="div" id={tableHeaderId} role="rowgroup">
        {headerGroups.map((headerGroup) => (
          // key should be provided by `react-table` via `getHeaderGroupProps`
          // eslint-disable-next-line react/jsx-key
          <Tr as="div" role="row" {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map(renderHeader)}
          </Tr>
        ))}
      </Thead>
      {isLoading ? (
        <Stack alignItems="center" marginTop="10%">
          <Spinner />
        </Stack>
      ) : (
        <div
          ref={parentRef}
          style={{
            overflow: 'auto',
            height: tableHeight,
          }}>
          <Tbody
            as="div"
            role="rowgroup"
            style={{
              height: tableBodyHeight,
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
            }}
            sx={bodyStyle}
            {...getTableBodyProps()}>
            {isTableEmpty ? (
              <Tr as="div" borderBottom="none" pt="15%" role="row">
                <Td as="div" role="cell" textAlign="center">
                  {emptyTableComponent}
                </Td>
              </Tr>
            ) : (
              displayRows
            )}
          </Tbody>
          {
            isFetchingNextPage && <Flex direction="column">
              <Center maxH="50%">
                <Spinner />
              </Center>
            </Flex>
          }
        </div>
      )}
    </ChakraTable>
  )
}

export { Table }
